import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosAuth from '../../../utils/axiosWithAuth';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Paper,
  Alert,
  Button,
  TextField,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import UserContext from '../../../contexts/UserContext';
import AuthorizationCreateAccountForm from './AuthorizationCreateAccountForm';
import TextractContext from '../../../contexts/TextractContext';
import AuthorizationAppBar from "./AuthorizationAppBar"
import AuthorizationStatusChip from './AuthorizationStatusChip';
import { formatUrl } from '../../helpers/string-helpers';

export default function Authorization() {
  const { user } = useContext(UserContext);
  const { utilityCompanies } = useContext(TextractContext);
  const { id } = useParams();
  const [authorization, setAuthorization] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataStartDate, setDataStartDate] = useState(null)

  // Fetch authorization data
  const fetchAuthorization = async () => {
    setLoading(true);
    try {
      const response = await axiosAuth().get(`/authorizations/${id}`);
      setAuthorization(response.data);
      setError(null);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('Authorization not found');
      } else {
        setError('Failed to load authorization');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthorization();
  }, [id]);

  // Initialize formData when authorization data is loaded
  useEffect(() => {
    if (authorization) {
      setFormData({
        online_access_url: authorization.online_access_url,
        online_access_username: authorization.online_access_username,
        online_access_password: authorization.online_access_password,
        status: authorization.status,
        errors: authorization.errors,
        other_details: authorization.other_details,
        two_fa_instructions: authorization.two_fa_instructions
      });
    }
  }, [authorization]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosAuth().put(`/authorizations/${id}`, formData);
      await fetchAuthorization();
    } catch (err) {
      console.error('Failed to update authorization:', err);
      setError('Failed to update authorization');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (accountId, newStatus) => {
    setAuthorization((prev) => ({
      ...prev,
      accounts: prev.accounts.map((acc) =>
        acc.id === accountId ? { ...acc, status: newStatus } : acc
      ),
    }));
    axiosAuth()
      .put(`/authorizations/${id}/update_account_status`, {
        utility_account_id: accountId,
        status: newStatus,
        data_start_date: dataStartDate
      })
      .then(() => {
        console.log('Status updated successfully');
      })
      .catch((err) => {
        console.error('Failed to update status:', err);
      });
  };

  // Update handleDataStartDateChange to mirror handleStatusChange
  const handleDataStartDateChange = (accountId, newStartDate) => {
    setAuthorization((prev) => ({
      ...prev,
      accounts: prev.accounts.map((acc) =>
        acc.id === accountId ? { ...acc, data_start_date: newStartDate } : acc
      ),
    }));
    axiosAuth()
      .put(`/authorizations/${id}/update_account_status`, {
        utility_account_id: accountId,
        data_start_date: newStartDate, // Use the new value directly
      })
      .then(() => {
        console.log('Data start date updated successfully');
      })
      .catch((err) => {
        console.error('Failed to update data start date:', err);
      });
  };

  return (
    <>
    
      <AuthorizationAppBar />
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <Button component={Link} to="/documents/authorizations" variant="outlined" startIcon={<ArrowBackIcon />}>
            Back
          </Button>
          <Typography variant="h5" style={{ marginLeft: '20px' }}>
            Authorization Details
          </Typography>
        </div>

        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        ) : authorization ? (
          <>
            <Card elevation={3} style={{ marginBottom: '20px', maxWidth: 600 }}>
              <CardContent style={{textAlign: 'left'}}>
                {user.admin ? (
                  <>
                    <TextField
                      label="Online Access URL"
                      value={formData.online_access_url || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, online_access_url: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Username"
                      value={formData.online_access_username || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, online_access_username: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Password"
                      value={formData.online_access_password || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, online_access_password: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                    />
                    {formData.two_fa_instructions && 
                      <>
                        <Typography>2FA Instructions:</Typography>
                        <Typography>{formData.two_fa_instructions}</Typography>
                      </>
                    }
                    {formData.other_details && 
                      <>
                        <Typography>Other Details:</Typography>
                        <Typography>{formData.other_details}</Typography>
                      </>
                    }
                    <Select
                      value={formData.status || ''}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    >
                      <MenuItem value="Inventory Pending">Inventory Pending</MenuItem>
                      <MenuItem value="Processing Inventory">Processing Inventory</MenuItem>
                      <MenuItem value="Unable To Access">Unable To Access</MenuItem>
                      <MenuItem value="Inventory Complete">Inventory Complete</MenuItem>
                    </Select>
                    <TextField
                      multiline
                      label="Errors"
                      type="errors"
                      value={formData.errors || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, errors: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ marginTop: '20px' }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="body1">
                      <strong>Online Access URL:</strong>{' '}
                      <a
                        href={formatUrl(authorization.online_access_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {authorization.online_access_url}
                      </a>
                    </Typography>
                    <Typography variant="body1">
                      <strong>Username:</strong> {authorization.online_access_username}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Status:</strong>{' '}
                      <AuthorizationStatusChip status={authorization.status} />
                    </Typography>
                  </>
                )}
                {authorization.errors && (
                  <Typography variant="body1">
                    <strong>Errors:</strong>
                    <Alert severity="error">{authorization.errors}</Alert>
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Typography variant="h6" gutterBottom>
              Accounts
            </Typography>
            {user.admin && (
              <AuthorizationCreateAccountForm
                utilityCompanies={utilityCompanies}
                fetchAuthorization={fetchAuthorization}
                authorization={authorization}
              />
            )}
            {authorization.accounts.length === 0 ? (
              <Typography variant="body1">No accounts found for this authorization.</Typography>
            ) : (
              <TableContainer component={Paper} elevation={2}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Account Number</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Utility Company</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Data Start Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {authorization.accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.acct_num}</TableCell>
                        <TableCell>{account.utility_company.name}</TableCell>
                        <TableCell>
                          <TextField
                            name="data_start_date"
                            type="date"
                            value={account.data_start_date ? account.data_start_date.split("T")[0] : ''} // Use account-specific data_start_date
                            onChange={(e) => handleDataStartDateChange(account.id, e.target.value)}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              style: { padding: '8px' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={account.status}
                            onChange={(e) => handleStatusChange(account.id, e.target.value)}
                            variant="outlined"
                            size="small"
                            style={{ minWidth: '120px' }}
                          >
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Inactive">Inactive</MenuItem>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}